import { wrap } from "@reatom/core"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createGachaEvmBuybackPriceQueryOptions } from "./gacha-evm-buyback-price"
import type { GachaEvmCardIdentity } from "./portfolio/gacha-evm-portfolio"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaEvmBuybackAvailability } from "./use-gacha-evm-buyback-availability"
import { useGachaNetworkContext } from "./use-gacha-network"

export function useGachaEvmBuybackPrice({
  identity,
  walletAddress,
  paymentToken,
  enabled,
  retryTransient = false,
}: {
  identity: GachaEvmCardIdentity
  walletAddress: string
  paymentToken?: string
  enabled: boolean
  retryTransient?: boolean
}) {
  const network = useGachaNetworkContext()
  const apiOptions = useGachaApiOptions()
  const queryClient = useQueryClient()
  const availability = useGachaEvmBuybackAvailability({
    environment: network.environment,
    identity,
    walletAddress,
    apiOptions,
    enabled: enabled && network.network === identity.chainId,
  })
  const priceOptions = createGachaEvmBuybackPriceQueryOptions(queryClient, {
    environment: network.environment ?? undefined,
    identity,
    walletAddress,
    paymentToken,
    apiOptions,
    eligibilityUntil: availability.eligibilityUntil,
    retryTransient,
  })
  const price = useQuery({
    ...priceOptions,
    enabled:
      enabled &&
      Boolean(network.environment) &&
      availability.eligible === true &&
      network.network === identity.chainId,
  })
  const refreshOffer = async () => {
    if (
      !enabled ||
      !network.environment ||
      network.network !== identity.chainId
    )
      return
    const fresh = await wrap(availability.query.refetch())
    if (
      fresh.isError ||
      !fresh.data?.available ||
      fresh.data.eligibilityUntil === undefined ||
      fresh.data.eligibilityUntil <= Date.now()
    )
      return
    await wrap(
      queryClient.fetchQuery(
        createGachaEvmBuybackPriceQueryOptions(queryClient, {
          environment: network.environment,
          identity,
          walletAddress,
          paymentToken,
          apiOptions,
          eligibilityUntil: fresh.data.eligibilityUntil,
          retryTransient,
        }),
      ),
    )
  }
  return { availability, price, priceOptions, refreshOffer }
}
